import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Step } from '|/app/services';

interface UseNavRailScrollProps {
  latestStep: Step | undefined;
  navRailRef: React.RefObject<HTMLElement>;
}

// Store scroll position globally to persist across component remounts
const savedScrollPosition = { value: 0 };

const useNavRailScroll = ({ latestStep, navRailRef }: UseNavRailScrollProps) => {
  const previousStepIdRef = useRef<string | undefined>(undefined);
  const hasScrolledForStepRef = useRef<Set<string>>(new Set());
  const location = useLocation();
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  const isNextStepRoute = location.pathname === '/next-step';

  // Save scroll position whenever it changes
  useEffect(() => {
    if (!isDesktop || !navRailRef.current) {
      return;
    }

    const navRail = navRailRef.current;

    const handleScroll = () => {
      savedScrollPosition.value = navRail.scrollTop;
    };

    navRail.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      navRail.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop, navRailRef]);

  // Restore scroll position on mount or when on /next-step
  // Use layout effect so we set scrollTop before paint to avoid a visible jump
  useLayoutEffect(() => {
    if (!isDesktop || !navRailRef.current) {
      return;
    }

    const navRail = navRailRef.current;
    const scrollPosition = savedScrollPosition.value;

    if (scrollPosition > 0) {
      // Clamp to max to avoid overscroll after content changes
      const maxScrollTop = Math.max(navRail.scrollHeight - navRail.clientHeight, 0);
      const clampedScrollTop = Math.min(scrollPosition, maxScrollTop);

      // eslint-disable-next-line react-compiler/react-compiler
      navRail.scrollTop = clampedScrollTop;
    }
  }, [isDesktop, navRailRef, location.pathname]);

  // Handle automatic scrolling to active step
  useEffect(() => {
    // Only run on desktop
    if (!isDesktop || !latestStep || !navRailRef.current) {
      return;
    }

    // Don't auto-scroll on /next-step route - preserve position
    if (isNextStepRoute) {
      return;
    }

    // Only scroll if the step has actually changed
    if (previousStepIdRef.current === latestStep.id) {
      return;
    }

    // Check if we're actually on the step URL before scrolling
    const stepUrl = latestStep.url;
    if (!location.pathname.includes(stepUrl)) {
      return;
    }

    // Check if we've already scrolled for this step
    if (hasScrolledForStepRef.current.has(latestStep.id)) {
      previousStepIdRef.current = latestStep.id;
      return;
    }

    previousStepIdRef.current = latestStep.id;

    // Find the active step element in the nav rail
    const activeStepElement = navRailRef.current.querySelector(
      `[data-analytics-id="pp_navrail_step_${stepUrl}"]`
    );

    if (activeStepElement) {
      // Longer delay to ensure step has fully rendered
      const timeoutId = setTimeout(() => {
        if (!navRailRef.current) {
          return;
        }

        const container = navRailRef.current;
        const el = activeStepElement as HTMLElement;

        // Only scroll if the element is out of view
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const margin = 24; // px padding to ensure it's comfortably in frame
        const isOutOfView = elRect.top < containerRect.top || elRect.bottom > containerRect.bottom;
        const nearTopEdge = elRect.top - containerRect.top < margin;
        const nearBottomEdge = containerRect.bottom - elRect.bottom < margin;

        if (isOutOfView || nearTopEdge || nearBottomEdge) {
          // Center the active step within the container for better visibility
          const currentScrollTop = container.scrollTop;
          const offsetFromContainerTop = elRect.top - containerRect.top;
          const target =
            currentScrollTop +
            offsetFromContainerTop -
            (container.clientHeight / 2 - el.clientHeight / 2);

          const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
          const nextTop = Math.max(0, Math.min(target, maxScrollTop));

          container.scrollTo({ top: nextTop, behavior: 'smooth' });
        }

        // Mark this step as scrolled
        hasScrolledForStepRef.current.add(latestStep.id);
      }, 300);

      // Cleanup timeout on unmount or dependency change
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [latestStep, navRailRef, isDesktop, location.pathname, isNextStepRoute]);
};

export default useNavRailScroll;
